import { useState, useRef } from 'react';
import { Upload, User, Building2, X, Check, Camera } from 'lucide-react';
import { createUserPhotoThumbnail, createCompanyLogoThumbnail, validateThumbnailFile } from '../../utils/thumbnailUtils';
import { toast } from 'react-toastify';
import { useLanguage } from '../../context/LanguageContext';

interface ThumbnailUploadProps {
  type: 'user' | 'company';
  currentImage?: string;
  onImageChange: (imageDataUrl: string | null) => void;
  disabled?: boolean;
  className?: string;
}

const ThumbnailUpload = ({ type, currentImage, onImageChange, disabled = false, className = '' }: ThumbnailUploadProps) => {
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!validateThumbnailFile(file, 10)) {
      toast.error('Please select a valid image file (JPEG, PNG, WebP, GIF) under 10MB');
      return;
    }

    try {
      setUploading(true);
      
      // Create thumbnail based on type
      const thumbnailDataUrl = type === 'user' 
        ? await createUserPhotoThumbnail(file)
        : await createCompanyLogoThumbnail(file);
      
      setPreview(thumbnailDataUrl);
      onImageChange(thumbnailDataUrl);
      
      toast.success(`${type === 'user' ? t('profile.userPhoto') : t('profile.companyLogo')} uploaded successfully!`);
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Error processing image. Please try again.');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    setPreview(null);
    onImageChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const isUser = type === 'user';
  const Icon = isUser ? User : Building2;
  const label = isUser ? t('profile.userPhoto') : t('profile.companyLogo');
  const description = isUser 
    ? t('profile.userPhotoDesc')
    : t('profile.companyLogoDesc');
  const requirement = isUser ? t('profile.userPhotoRecommended') : t('profile.companyLogoOptional');

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label} ({requirement})
      </label>
      
      <div className="flex items-start space-x-4">
        {/* Image Preview/Placeholder */}
        <div className="relative">
          <div 
            className={`relative overflow-hidden border-2 border-dashed border-gray-300 transition-all duration-200 ${
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary-400'
            } ${isUser ? 'rounded-full w-24 h-24' : 'rounded-lg w-24 h-24'}`}
            onClick={handleClick}
          >
            {preview ? (
              <img
                src={preview}
                alt={label}
                className={`w-full h-full object-cover ${isUser ? 'rounded-full' : 'rounded-lg'}`}
              />
            ) : (
              <div className={`w-full h-full bg-gray-100 flex items-center justify-center ${isUser ? 'rounded-full' : 'rounded-lg'}`}>
                <Icon className="h-8 w-8 text-gray-400" />
              </div>
            )}
            
            {/* Upload overlay */}
            {!disabled && (
              <div className={`absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center ${isUser ? 'rounded-full' : 'rounded-lg'}`}>
                <div className="opacity-0 hover:opacity-100 transition-opacity duration-200">
                  {uploading ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <Camera className="h-6 w-6 text-white" />
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Remove button */}
          {preview && !disabled && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveImage();
              }}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-lg"
              aria-label={t('profile.removeImage')}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Upload Instructions */}
        <div className="flex-1">
          <div className="space-y-2">
            <p className="text-sm text-gray-600">{description}</p>
            
            <div className="space-y-1 text-xs text-gray-500">
              <p>• {t('profile.imageRequirements').replace('{size}', isUser ? '400×400' : '300×300')}</p>
              <p>• {t('profile.maxFileSize')}</p>
              <p>• {t('profile.supportedFormats')}</p>
              {isUser && <p>• {t('profile.squareImagesWork')}</p>}
            </div>
            
            {!disabled && (
              <button
                onClick={handleClick}
                disabled={uploading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-600 mr-2"></div>
                    {t('profile.processing')}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    {preview 
                      ? t('profile.changeImage').replace('{type}', label)
                      : t('profile.uploadImage').replace('{type}', label)
                    }
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || uploading}
      />
    </div>
  );
};

export default ThumbnailUpload;